import { useEffect, useState } from "react";

export interface ImageUploadFieldProps {
  imageFile: File | null;
  onImageFileChange: (file: File | null) => void;
  existingImageUrl?: string | null;
  placeholderImageUrl: string;
  label?: string;
  accept?: string;
  helperText?: string;
  autoDefaultFileUrl?: string;
  mode: "create" | "edit";
  onAutoDefaultFileResolved?: (file: File) => void;
}

export default function ImageUploadField({
  imageFile,
  onImageFileChange,
  existingImageUrl,
  placeholderImageUrl,
  label = "Upload Image",
  accept = "image/*",
  helperText = "JPG, GIF or PNG. Max size of 800K",
  autoDefaultFileUrl,
  mode,
  onAutoDefaultFileResolved,
}: ImageUploadFieldProps) {
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    if (!imageFile) {
      setImagePreview("");
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  useEffect(() => {
    if (!autoDefaultFileUrl || mode !== "create") return;
    const loadDefaultFile = async () => {
      try {
        const response = await fetch(autoDefaultFileUrl);
        const blob = await response.blob();
        const fileName = autoDefaultFileUrl.split("/").pop() || "default.png";
        const file = new File([blob], fileName, {
          type: blob.type || "image/png",
        });
        onAutoDefaultFileResolved?.(file);
      } catch (error) {
        console.error("Error loading default image:", error);
      }
    };
    loadDefaultFile();
  }, [autoDefaultFileUrl, mode, onAutoDefaultFileResolved]);

  return (
    <div className="mb-3">
      <div className="profile-upload">
        <div
          className="profile-upload-img"
          style={{
            backgroundImage: `url(${
              imagePreview || existingImageUrl || placeholderImageUrl
            })`,
            width: 120,
            height: 120,
            backgroundSize: "cover",
            backgroundPosition: "center",
            border:
              imagePreview || existingImageUrl ? "none" : "2px dashed #ced4da",
          }}
        >
          {!!imagePreview && (
            <button
              type="button"
              className="profile-remove"
              onClick={() => {
                onImageFileChange(null);
              }}
            >
              <i className="feather-x" />
            </button>
          )}
        </div>

        <div className="profile-upload-content">
          <label className="profile-upload-btn">
            <i className="ti ti-file-broken" /> {label}
            <input
              type="file"
              accept={accept}
              className="input-img"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                onImageFileChange(file);
              }}
            />
          </label>
          <p>{helperText}</p>
        </div>
      </div>
    </div>
  );
}
